<template>
  <div class="min-h-screen bg-zinc-950 p-8 text-zinc-100">
    <div class="max-w-6xl mx-auto space-y-8">
      
      <!-- Header -->
      <header class="flex justify-between items-center bg-zinc-900/50 backdrop-blur-md p-6 rounded-3xl shadow-sm border border-zinc-800/50 relative overflow-hidden">
        <div class="relative z-10 w-full flex justify-between items-start">
          <div>
            <router-link to="/" class="inline-flex items-center gap-2 text-zinc-400 hover:text-green-400 transition-colors mb-2 text-sm font-medium">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
              Retour
            </router-link>
            <h1 class="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-600">Statistiques Musicales</h1>
            <p class="text-zinc-400 mt-1">Analyse de vos titres likés sur Spotify</p>
          </div>
          
          <router-link :to="{ name: 'music_my_playlists' }" class="bg-zinc-800 hover:bg-zinc-700 text-emerald-400 border border-emerald-500/30 font-bold py-2.5 px-5 rounded-xl text-sm flex items-center gap-2 transition-all shadow-md">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"></path></svg>
            Gérer mes playlists
          </router-link>
        </div>
        
        <!-- Decorative blob -->
        <div class="absolute -right-10 -top-10 w-48 h-48 bg-green-500/10 rounded-full blur-3xl"></div>
      </header>

      <div v-if="loading" class="text-center py-20">
        <div class="w-16 h-16 border-4 border-green-500/20 border-t-green-500 rounded-full animate-spin mx-auto mb-4"></div>
        <p class="text-zinc-500 animate-pulse">Analyse de vos goûts musicaux...</p>
      </div>

      <div v-else-if="error" class="bg-red-500/10 border border-red-500/20 p-8 rounded-3xl text-center">
        <p class="text-red-400">{{ error }}</p>
      </div>

      <div v-else class="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        <!-- Top Artists Section -->
        <div class="lg:col-span-7 space-y-6">
          <div class="flex items-center justify-between">
            <h2 class="text-2xl font-bold flex items-center gap-3">
              <span class="p-2 bg-green-500/10 rounded-xl text-green-500">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"></path></svg>
              </span>
              Top Artistes
            </h2>
            <div class="flex items-center gap-6">
              <router-link :to="{ name: 'music_all_artists' }" class="text-sm text-green-400 hover:text-green-300 transition-colors font-medium hover:underline decoration-green-400/30 underline-offset-4">
                Voir tout ({{ totals.total_artists }}) &rarr;
              </router-link>
              <router-link :to="{ name: 'MissingMbArtists' }" class="text-sm text-rose-400 hover:text-rose-300 transition-colors font-medium hover:underline decoration-rose-400/30 underline-offset-4" title="Artistes non trouvés sur MusicBrainz">
                MBID Manquants
              </router-link>
              <div class="flex gap-4 text-sm font-medium text-zinc-400">
                <div class="flex flex-col items-end">
                  <span class="text-xs text-zinc-500 uppercase tracking-wider">Artistes</span>
                  <span class="text-lg text-zinc-200">{{ totals.total_artists }}</span>
                </div>
                <div class="w-px bg-zinc-800"></div>
                <div class="flex flex-col items-end">
                  <span class="text-xs text-zinc-500 uppercase tracking-wider">Titres</span>
                  <span class="text-lg text-zinc-200">{{ totals.total_tracks }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="bg-zinc-900/40 backdrop-blur-sm rounded-3xl border border-zinc-800/50 p-2 space-y-1">
            <router-link 
              v-for="(artist, index) in topArtists" 
              :key="artist.spotify_id"
              :to="{ name: 'music_artist_detail', params: { id: artist.spotify_id } }"
              class="group flex items-center gap-4 p-4 rounded-2xl hover:bg-zinc-800/50 transition-all duration-300 relative overflow-hidden block cursor-pointer hover:scale-[1.01]"
            >
              <!-- Rank -->
              <div class="w-8 text-center font-black text-xl" :class="index < 3 ? 'text-green-400' : 'text-zinc-600'">
                #{{ index + 1 }}
              </div>
              
              <!-- Image -->
              <div class="w-14 h-14 rounded-full overflow-hidden bg-zinc-800 shadow-md ring-2 ring-transparent group-hover:ring-green-500/30 transition-all">
                <img v-if="artist.images && artist.images.length > 0" :src="artist.images[artist.images.length - 1].url" class="w-full h-full object-cover" />
                <div v-else class="w-full h-full flex items-center justify-center text-zinc-600">
                  <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
                </div>
              </div>
              
              <!-- Info -->
              <div class="flex-1 min-w-0 z-10">
                <h3 class="font-bold text-lg text-zinc-200 truncate group-hover:text-green-400 transition-colors">{{ artist.name }}</h3>
                <p class="text-sm text-zinc-500">{{ artist.track_count }} titres likés</p>
              </div>

              <!-- Progress Bar Background -->
              <div class="absolute bottom-0 left-0 h-full bg-green-500/5 transition-all duration-1000 ease-out z-0" :style="{ width: `${(artist.track_count / maxArtistCount) * 100}%` }"></div>
            </router-link>

            <div v-if="topArtists.length === 0" class="p-8 text-center text-zinc-500">
              Aucun artiste trouvé. Veuillez synchroniser votre compte.
            </div>
          </div>
        </div>

        <!-- Top Genres Section -->
        <div class="lg:col-span-5 space-y-6">
          <div class="flex items-center justify-between">
            <h2 class="text-2xl font-bold flex items-center gap-3">
              <span class="p-2 bg-emerald-500/10 rounded-xl text-emerald-500">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"></path></svg>
              </span>
              Top Genres
            </h2>
            <router-link :to="{ name: 'music_all_genres' }" class="text-sm text-emerald-400 hover:text-emerald-300 transition-colors font-medium hover:underline decoration-emerald-400/30 underline-offset-4">
              Voir tout &rarr;
            </router-link>
          </div>

          <div class="bg-zinc-900/40 backdrop-blur-sm rounded-3xl border border-zinc-800/50 p-6 space-y-6">
            <router-link 
              v-for="(genre, index) in topGenres" 
              :key="genre.id"
              :to="{ name: 'music_genre_detail', params: { id: genre.id } }"
              class="space-y-2 group block cursor-pointer hover:scale-[1.01] transition-transform"
            >
              <div class="flex justify-between items-end text-sm">
                <span class="font-medium text-zinc-300 capitalize group-hover:text-emerald-400 transition-colors">{{ genre.name }}</span>
                <span class="text-zinc-500 font-mono">{{ genre.score }}</span>
              </div>
              
              <div class="h-3 w-full bg-zinc-800 rounded-full overflow-hidden">
                <div 
                  class="h-full rounded-full transition-all duration-1000 ease-out"
                  :class="index === 0 ? 'bg-gradient-to-r from-emerald-500 to-green-400' : 'bg-zinc-600 group-hover:bg-emerald-500/80'"
                  :style="{ width: `${(genre.score / maxGenreCount) * 100}%` }"
                ></div>
              </div>
            </router-link>

            <div v-if="topGenres.length === 0" class="text-center text-zinc-500 py-4">
              Aucun genre trouvé.
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useToast } from '../../composables/useToast'

const { error: showError } = useToast()
const loading = ref(true)
const error = ref('')

interface Artist {
  spotify_id: string
  name: string
  images: Array<{url: string, height: number, width: number}>
  track_count: number
}

interface Genre {
  id: number
  name: string
  score: number
}

const topArtists = ref<Artist[]>([])
const topGenres = ref<Genre[]>([])
const totals = ref({ total_tracks: 0, total_artists: 0 })

const maxArtistCount = computed(() => {
  if (topArtists.value.length === 0) return 1
  return Math.max(...topArtists.value.map(a => a.track_count))
})

const maxGenreCount = computed(() => {
  if (topGenres.value.length === 0) return 1
  return Math.max(...topGenres.value.map(g => g.score))
})

const fetchAnalytics = async () => {
  try {
    const token = localStorage.getItem('apiToken')
    const response = await fetch('/api/music/analytics', {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    
    if (!response.ok) throw new Error('Erreur réseau')
    
    const data = await response.json()
    topArtists.value = data.topArtists || []
    topGenres.value = data.topGenres || []
    if (data.totals) {
      totals.value = data.totals
    }
  } catch (e) {
    console.error(e)
    error.value = "Impossible de charger les statistiques."
    showError(error.value)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchAnalytics()
})
</script>
